<template>
  <el-dialog :visible.sync="dialogVisible" :title="$t(isEdit ? 'operation.edit_business' : 'operation.create_business')" width="640px" :close-on-click-modal="false" @closed="resetForm">
    <el-form ref="businessForm" :model="form" :rules="rules" label-width="110px">
      <el-form-item :label="$t('operation.business_name')" prop="business_name">
        <el-input v-model="form.business_name" maxlength="128" show-word-limit :placeholder="$t('operation.enter_business_name')" />
      </el-form-item>
      <el-form-item :label="$t('operation.business_type')" prop="business_type">
        <el-radio-group v-model="form.business_type" @change="onBusinessTypeChange">
          <el-radio :label="1">{{ $t('operation.self_operated') }}</el-radio>
          <el-radio :label="2">{{ $t('operation.franchise') }}</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item :label="$t('operation.business_region')" prop="country_code">
        <el-select v-model="form.country_code" :disabled="isEdit" :loading="regionLoading" :placeholder="$t('operation.select_region')" style="width: 100%" @change="onRegionChange">
          <el-option v-for="region in regionOptions" :key="region.value" :label="region.label" :value="region.value" />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('operation.business_currency')" prop="currency">
        <el-select v-model="form.currency" :disabled="isEdit || !form.country_code" :placeholder="$t('operation.select_currency')" style="width: 100%">
          <el-option v-for="currencyOption in currencyOptions" :key="currencyOption" :label="currencyOption" :value="currencyOption" />
        </el-select>
      </el-form-item>
      <el-form-item v-if="form.business_type === 2" :label="$t('operation.related_franchisee')" prop="franchisee_id">
        <el-select v-model="form.franchisee_id" :loading="franchiseeLoading" filterable :placeholder="$t('operation.select_franchisee')" style="width: 100%">
          <el-option v-for="franchisee in filteredFranchiseeOptions" :key="franchisee.franchisee_id" :label="formatFranchisee(franchisee)" :value="franchisee.franchisee_id" />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('operation.description')" prop="description">
        <el-input v-model="form.description" type="textarea" :rows="4" maxlength="512" show-word-limit :placeholder="$t('operation.enter_description')" />
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button @click="dialogVisible = false">{{ $t('components.cancel') }}</el-button>
      <el-button type="primary" :loading="submitting" @click="submit">{{ $t('components.confirm') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { createBusiness, updateBusiness, fetchBusinessDetail } from '@/api/operation-manage/business'
import { fetchFranchiseeList } from '@/api/operation-manage/franchisee'
import { fetchOnlineCountryList } from '@/api/components/components.js'

const getDefaultForm = () => ({
  business_no: '',
  business_name: '',
  business_type: 1,
  country_code: '',
  currency: '',
  franchisee_id: null,
  description: ''
})

export default {
  name: 'BusinessFormDialog',
  data() {
    return {
      dialogVisible: false,
      submitting: false,
      isEdit: false,
      regionLoading: false,
      franchiseeLoading: false,
      onlineCountryList: [],
      franchiseeOptions: [],
      form: getDefaultForm(),
      rules: {
        business_name: [{ required: true, message: this.$t('operation.enter_business_name'), trigger: 'blur' }],
        business_type: [{ required: true, message: this.$t('operation.select_business_type'), trigger: 'change' }],
        country_code: [{ required: true, message: this.$t('operation.select_region'), trigger: 'change' }],
        currency: [{ required: true, message: this.$t('operation.select_currency'), trigger: 'change' }],
        franchisee_id: [{ required: true, message: this.$t('operation.select_franchisee'), trigger: 'change' }]
      }
    }
  },
  computed: {
    regionOptions() {
      const language = this.$store.getters.language
      const currentCountryCode = this.$store.getters.curCountry.country_code
      const availableCountries = !this.isEdit && currentCountryCode !== 'HK-USD'
        ? this.onlineCountryList.filter(country => country.code === currentCountryCode)
        : this.onlineCountryList
      return availableCountries.map(country => ({
        label: language === 'zh' ? country.cn_name : country.en_name,
        value: country.code
      }))
    },
    currencyOptions() {
      const currentCountryCode = this.$store.getters.curCountry.country_code
      if (currentCountryCode === 'HK-USD') {
        return [...new Set(this.onlineCountryList.reduce((currencies, country) => {
          const countryCurrencies = Array.isArray(country.currency)
            ? country.currency
            : String(country.currency || '').split(',')
          return currencies.concat(countryCurrencies.map(currency => currency.trim()).filter(Boolean))
        }, []))]
      }
      const region = this.onlineCountryList.find(country => country.code === this.form.country_code)
      if (!region || !region.currency) return []
      return Array.isArray(region.currency)
        ? region.currency
        : String(region.currency).split(',').map(currencyValue => currencyValue.trim()).filter(Boolean)
    },
    filteredFranchiseeOptions() {
      if (this.form.business_type !== 2 || !this.form.country_code) return []
      return this.franchiseeOptions.filter(franchisee => franchisee.country_code === this.form.country_code)
    }
  },
  methods: {
    showModal(business) {
      this.isEdit = Boolean(business)
      this.dialogVisible = true
      this.getOnlineCountryList()
      this.getFranchiseeList()
      if (business) {
        // 先设置业务的基本字段，等加盟商列表加载完再回显 franchisee_id
        this.form = { ...getDefaultForm(), ...business }
        this.loadBusinessDetail(business)
      } else {
        this.form = getDefaultForm()
      }
    },
    loadBusinessDetail(business) {
      if (!business.business_no) return
      Promise.all([
        fetchBusinessDetail(business.business_no)
      ]).then(([response]) => {
        if (response.code === 10000) {
          const detail = response.data
          this.form = {
            ...detail,
            franchisee_id: this.findFranchiseeId(detail.franchisee_no)
          }
        }
      })
    },
    getOnlineCountryList() {
      this.regionLoading = true
      return fetchOnlineCountryList().then(response => {
        this.onlineCountryList = response.data || []
      }).finally(() => {
        this.regionLoading = false
      })
    },
    getFranchiseeList() {
      this.franchiseeLoading = true
      return fetchFranchiseeList({ page: 1, size: 999 }).then(response => {
        const franchiseeData = response.data || {}
        this.franchiseeOptions = franchiseeData.list || []
      }).finally(() => {
        this.franchiseeLoading = false
      })
    },
    formatFranchisee(franchisee) {
      return [franchisee.franchisee_no, franchisee.franchisee_name].filter(Boolean).join(' / ')
    },
    findFranchiseeId(franchiseeNo) {
      if (!franchiseeNo) return null
      const franchisee = this.franchiseeOptions.find(option => option.franchisee_no === franchiseeNo)
      return franchisee ? franchisee.franchisee_id : null
    },
    onBusinessTypeChange(businessType) {
      if (businessType === 1) this.form.franchisee_id = null
    },
    onRegionChange() {
      this.form.currency = ''
      if (this.form.business_type === 2) this.form.franchisee_id = null
    },
    resetForm() {
      this.form = getDefaultForm()
      this.isEdit = false
      this.$refs.businessForm && this.$refs.businessForm.resetFields()
    },
    submit() {
      this.$refs.businessForm.validate(valid => {
        if (!valid) return
        this.submitting = true
        const requestData = {
          ...this.form,
          business_no: this.form.business_no || undefined,
          franchisee_id: this.form.business_type === 2 ? this.form.franchisee_id : null
        }
        if (!this.isEdit) delete requestData.business_no
        const submitRequest = this.isEdit ? updateBusiness : createBusiness
        submitRequest(requestData).then(response => {
          if (response.code === 10000) {
            this.$message.success(this.$t(this.isEdit ? 'operation.edit_success' : 'operation.create_success'))
            this.dialogVisible = false
            this.$emit('success')
          }
        }).finally(() => {
          this.submitting = false
        })
      })
    }
  }
}
</script>
